import {
	Component,
	Input,
	ElementRef,
	ViewEncapsulation,
	ViewChild,
	AfterViewInit,
	OnDestroy
} from '@angular/core';
import { EVENTS } from '@constants/analytics';
import { computePosition, offset, shift, Placement } from '@floating-ui/dom';
import { AnalyticsService } from '@services/analytics.service';

@Component({
	standalone: false,
	selector: 'qe-tooltip',
	templateUrl: './tooltip.component.html',
	styleUrls: ['./tooltip.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
	@Input() tooltipId!: string;
	@Input() containerClass?: string;
	@Input() placement?: Placement = 'bottom-start';
	@Input() triggerAction?: 'hover' | 'click' = 'hover';

	@ViewChild('trigger', { static: true }) triggerRef!: ElementRef;
	@ViewChild('tooltip', { static: false }) tooltipRef!: ElementRef;

	public isTooltipHovered: boolean = false;

	constructor(
		private readonly elementRef: ElementRef,
		private readonly gtmService: AnalyticsService,
	) {
		this.addOutsideClickListener();
	}

	ngAfterViewInit(): void {
		if (this.isTooltipHovered && this.tooltipRef) {
			this.updatePosition();
		}
	}

	ngOnDestroy(): void {
		document.removeEventListener('click', this.onClickOutside);
	}

	public onTriggerClick(event: MouseEvent): void {
		event.preventDefault();
		this.isTooltipHovered = true;

		setTimeout(() => this.updatePosition(), 0);

		// Send the tooltip event to GTM
		this.gtmService.push({
			event: EVENTS.TOOLTIP,
			tooltip_id: this.tooltipId
		});
	}

	public addOutsideClickListener(): void {
		this.onClickOutside = (event: MouseEvent): void => {
			if (!this.isClickInsideComponent(event) && this.isTooltipHovered) {
				this.isTooltipHovered = false;
			}
		};

		document.addEventListener('click', this.onClickOutside);
	}

	public onTooltipEnter(): void {
		if (this.triggerAction === 'click') return;
		this.isTooltipHovered = true;

		setTimeout(() => this.updatePosition(), 0);

		// Send the tooltip event to GTM
		this.gtmService.push({
			event: EVENTS.TOOLTIP,
			tooltip_id: this.tooltipId
		});
	}

	public onTooltipLeave(): void {
		if (this.triggerAction === 'click') return;
		this.isTooltipHovered = false;
	}

	private async updatePosition(): Promise<void> {
		if (!this.triggerRef || !this.tooltipRef) {
			return;
		}

		const oValue = 4;
		const { x, y } = await computePosition(
			this.triggerRef.nativeElement,
			this.tooltipRef.nativeElement,
			{
				placement: this.placement,
				middleware: [shift(), offset(oValue)]
			}
		);

		Object.assign(this.tooltipRef.nativeElement.style, {
			left: `${x < 0 ? x - oValue : x}px`,
			top: `${y}px`
		});
	}

	private isClickInsideComponent(event: MouseEvent): boolean {
		const element = event.target as HTMLElement;

		return this.elementRef.nativeElement.contains(element);
	}

	private onClickOutside: (event: MouseEvent) => void = () => null;
}
